import React, { useState, useMemo } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import {
  IconButton,
  OutlinedInput,
  InputLabel,
  InputAdornment,
  FormControl,
  Button,
} from "@material-ui/core";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import axios from "../../../../config/axios/axios";
import PasswordChecklist from "react-password-checklist";
import { useHistory } from "react-router-dom";
import "./style.css";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  margin: {
    margin: theme.spacing(1),
  },
  textField: {
    width: "100%",
  },
}));

function ResetPasswordForm({ type, token }) {
  const history = useHistory();
  const classes = useStyles();
  const { t } = useTranslation();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCheckList, setShowCheckList] = useState(false);
  const [error, setError] = useState("");

  // ✅ Updated regex — universal special character match
  const passwordRegex = useMemo(
    () =>
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[ !"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]).{8,}$/,
    [],
  );

  const isPasswordValid = passwordRegex.test(password);
  const isMatch = password === confirmPassword;
  const canSubmit = isPasswordValid && isMatch;

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((prev) => !prev);
  const handleMouseDownPassword = (event) => event.preventDefault();

  const changePassword = async (e) => {
    e.preventDefault();
    setShowCheckList(true);
    setError("");

    const normalizedPassword = password.trim();
    const normalizedConfirm = confirmPassword.trim();

    if (!passwordRegex.test(normalizedPassword)) {
      setError(t("generals.passwordMustContain"));
      return;
    }

    if (normalizedPassword !== normalizedConfirm) {
      setError(t("generals.passwordDoNotMatch"));
      return;
    }

    try {
      const res = await axios.post(`client/${type}/password/change`, {
        token,
        password: normalizedPassword,
        confirmPassword: normalizedConfirm,
      });

      if (res.data === true) {
        history.push("/reset-successful");
      } else {
        setError(t("generals.unexpectedResponse"));
      }
    } catch (err) {
      console.error("Password reset error:", err);
      setError(err?.response?.data?.message || t("generals.errorOccurred"));
    }
  };

  return (
    <div className="container__reset">
      <form className="container__form" onSubmit={changePassword}>
        <h2>{t("generals.changePassword")}</h2>

        {/* Password */}
        <FormControl
          className={clsx(classes.margin, classes.textField)}
          variant="outlined"
        >
          <InputLabel htmlFor="password">{t("generals.password")}</InputLabel>
          <OutlinedInput
            id="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setShowCheckList(true);
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
            labelWidth={70}
          />
        </FormControl>

        {/* Confirm Password */}
        <FormControl
          className={clsx(classes.margin, classes.textField)}
          variant="outlined"
        >
          <InputLabel htmlFor="confirm-password">
            {t("generals.confirmPassword")}
          </InputLabel>
          <OutlinedInput
            id="confirm-password"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setShowCheckList(true);
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={handleClickShowConfirmPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
            labelWidth={135}
          />
        </FormControl>

        {error && (
          <p style={{ color: "red", fontSize: 14, marginTop: 5 }}>{error}</p>
        )}

        <Button
          type="submit"
          variant="contained"
          size="medium"
          color="primary"
          disabled={!canSubmit}
          className={classes.margin}
        >
          {t("generals.save")}
        </Button>

        {/* Password Checklist */}
        <div style={{ display: showCheckList ? "block" : "none" }}>
          <PasswordChecklist
            rules={["minLength", "specialChar", "number", "capital", "match"]}
            minLength={8}
            value={password}
            valueAgain={confirmPassword}
            messages={{
              minLength: t("generals.passwordMinLength"),
              specialChar: t("generals.passwordSpecialChar"),
              number: t("generals.passwordNumber"),
              capital: t("generals.passwordCapital"),
              match: t("generals.passwordMatch"),
            }}
          />
        </div>
      </form>
    </div>
  );
}

export default ResetPasswordForm;
